import { CheckoutLifecycleEventPostMessageName } from '@eventbrite/checkout-postmessage-api';
import { HAS_DOCUMENT } from '@eventbrite/eds-utils';
import {
    getWindowObject,
    HAS_WINDOW_LISTENERS,
} from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import { logEvent } from '@eventbrite/statsig';
import classNames from 'classnames';
import escape from 'lodash/escape';
import React, { useContext, useEffect, useState } from 'react';
import { STATSIG_EVENT_NAMES } from '../../../../constants';
import { isEventEligibleForRepeatingEventsConsumerExperiment } from '../../../../statsig';
import type { CheckoutWidgetProps, Event } from '../../../../types/index';
import { findFirstAvailableEvent } from '../../WhenAndWhere/DateAndTime/utils';
import './TicketSelectionIframe.scss';

import {
    EventBasicInformation,
    EventBasicInformationContext,
} from '../../../../contexts/EventContext';

export type TicketSelectionIframeProps = CheckoutWidgetProps & {
    eventName: Event['name'];
    isPreview: Event['isPreview'];
    childEvents: Event['childEvents'];
    isSeriesParent: Event['isSeriesParent'];
};

export const TICKET_SELECTION_CONTAINER_ID =
    'ticket-selection-iframe-container';
const COMPACT_CHECKOUT_PARAMS = { name: 'ticketsPageSize', value: 'compact' };
const INITIAL_COMPONENT_HEIGHT = 204;

export const TicketSelectionIframe = ({
    affiliateCode,
    campaign_token,
    childEvents,
    eventName,
    isPreview,
    isSeriesParent,
    promoCode,
    rsvpToken,
    waitlistToken,
}: TicketSelectionIframeProps) => {
    const { id: eventId } = useContext(
        EventBasicInformationContext,
    ) as EventBasicInformation;

    const isParentEventExperimentVariant =
        isEventEligibleForRepeatingEventsConsumerExperiment(
            isSeriesParent,
            childEvents,
        );

    const [iframeWrapperHeight, setIframeWrapperHeight] = useState(
        INITIAL_COMPONENT_HEIGHT,
    );
    const [isTicketSelectionPage, setIsTicketSelectionPage] = useState(true);

    useEffect(() => {
        const ebWidgets = getWindowObject('EBWidgets');
        if (!ebWidgets) return;

        ebWidgets.createWidget({
            widgetType: 'checkout',
            eventId:
                isParentEventExperimentVariant && childEvents?.length
                    ? findFirstAvailableEvent(childEvents)?.id
                    : eventId,
            iframeContainerId: TICKET_SELECTION_CONTAINER_ID,
            extraParams: [COMPACT_CHECKOUT_PARAMS],
            preventResizing: true,
            iframeContainerHeight: iframeWrapperHeight,
            iframeTitle: gettext('Checkout for %(eventName)s', {
                eventName,
            }),
            ...(!isPreview ? { affiliateCode } : {}),
            ...(promoCode ? { promoCode: escape(promoCode) } : {}),
            ...(waitlistToken ? { waitlistToken } : {}),
            ...(rsvpToken ? { rsvpToken } : {}),
            ...(campaign_token ? { campaign_token } : {}),
        });

        HAS_WINDOW_LISTENERS &&
            window.addEventListener('message', _handlePostMessages);

        logEvent(STATSIG_EVENT_NAMES.LISTING_TICKET_SELECTION_VIEW);

        return () => {
            HAS_WINDOW_LISTENERS &&
                window.removeEventListener('message', _handlePostMessages);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const bodyTag = HAS_DOCUMENT && document.querySelector('body');

        if (bodyTag) {
            !isTicketSelectionPage
                ? bodyTag.classList.add('checkout-app-modal-opened')
                : bodyTag.classList.remove('checkout-app-modal-opened');
        }
    }, [isTicketSelectionPage]);

    const _handlePostMessages = (event: MessageEvent) => {
        const {
            data: {
                messageName,
                contentHeight = INITIAL_COMPONENT_HEIGHT,
            } = {},
        } = event;

        switch (messageName) {
            case CheckoutLifecycleEventPostMessageName.ticketSelectionResize:
                setIframeWrapperHeight(contentHeight);
                break;
            case CheckoutLifecycleEventPostMessageName.checkoutFormLoaded:
            case CheckoutLifecycleEventPostMessageName.deliveryMethodFormLoaded:
            case CheckoutLifecycleEventPostMessageName.orderComplete:
            case CheckoutLifecycleEventPostMessageName.organizerQuestionsFormLoaded:
                setIsTicketSelectionPage(false);
                break;
            case CheckoutLifecycleEventPostMessageName.ticketSelectionCompactLoaded:
                setIsTicketSelectionPage(true);
                setIframeWrapperHeight(INITIAL_COMPONENT_HEIGHT);
                break;
            case CheckoutLifecycleEventPostMessageName.ticketSelectionSubmit:
                logEvent(STATSIG_EVENT_NAMES.LISTING_TICKET_SELECTION_CLICK);
                break;
            default:
                break;
        }
    };

    const _iframeWrapperHeightUnits = isTicketSelectionPage
        ? `${iframeWrapperHeight}px`
        : '100%';

    const classes = classNames({
        'conversion-bar--ticket-selection': isTicketSelectionPage,
        'conversion-bar--full-size': !isTicketSelectionPage,
    });

    return (
        <div
            id={TICKET_SELECTION_CONTAINER_ID}
            data-testid={TICKET_SELECTION_CONTAINER_ID}
            style={{ height: _iframeWrapperHeightUnits }}
            className={classes}
        ></div>
    );
};
